import { ChangeDetectorRef, Component, OnInit, DOCUMENT, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { AuthStore } from 'apps/viguide-planning/src/app/state/auth/auth.store';
import { Observable, take } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { normalizeCommonJSImport } from '@nx-customer-apps/shared/utils';
import { SettingsStore } from './state/settings/settings.store';
import { SnackbarService, WindowService } from '@nx-customer-apps/shared/services';
import { ProjectAccessStore } from './state/project-access/project-access.store';
import { RemoteConfigService, StatusPageService } from './services';

@Component({
    selector: 'vp-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
    private authStore = inject(AuthStore);
    private meta = inject(Meta);
    private title = inject(Title);
    private translateService = inject(TranslateService);
    private settingsStore = inject(SettingsStore);
    private projectAccessStore = inject(ProjectAccessStore);
    private windowService = inject(WindowService);
    private remoteConfigService = inject(RemoteConfigService);
    private statusPageService = inject(StatusPageService);
    private snackbarService = inject(SnackbarService);
    private swUpdate = inject(SwUpdate);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private document = inject<Document>(DOCUMENT);

    public authenticated: Observable<boolean>;
    public statusSummaryState$ = this.statusPageService.state$;

    constructor() {
        this.remoteConfigService.getConfiguration();
        this.checkForNewVersion();
        this.importScrollBehaviorPolyfill();
        this.authenticated = this.authStore.authenticated$.pipe(tap(() => this.changeDetectorRef.detectChanges()));
    }

    public ngOnInit(): void {
        this.authStore.startAuthFlow();
        this.settingsStore.initDefaultLocale();
        this.setMetaTags();
    }

    public logout(): void {
        this.projectAccessStore.unlockProjectBeforeUnload();
        this.authStore.clearToken();
    }

    private setMetaTags() {
        this.translateService
            .stream('COMMON.APP_NAME')
            .pipe(take(1))
            .subscribe(appName => {
                this.title.setTitle(appName);
                this.meta.addTag({ name: 'author', content: appName });
            });
    }

    private async importScrollBehaviorPolyfill() {
        if (!('scrollBehavior' in this.document.documentElement.style)) {
            /**
             * Scroll behavior polyfill
             * https://www.npmjs.com/package/scroll-behavior-polyfill
             */
            await normalizeCommonJSImport(import('scroll-behavior-polyfill'));
        }
    }

    private checkForNewVersion(): void {
        if (this.swUpdate.isEnabled) {
            this.swUpdate.checkForUpdate();
            this.swUpdate.versionUpdates
                .pipe(
                    filter(versionEvent => versionEvent.type === 'VERSION_READY'),
                    switchMap(() => this.translateService.get(['APP.NEW_VERSION', 'BUTTON.RELOAD'])),
                    map(keys => ({
                        message: keys['APP.NEW_VERSION'],
                        action: keys['BUTTON.RELOAD']
                    })),
                    switchMap(({ message, action }) =>
                        this.snackbarService
                            .showInfo(message, {
                                duration: 0,
                                action,
                                verticalPosition: 'top',
                                horizontalPosition: 'center'
                            })
                            .onAction()
                    )
                )
                .subscribe(() => {
                    this.authStore.logout();
                    this.windowService.nativeWindow.location.reload();
                });
        }
    }
}
